import React, { useState } from 'react';
import { Tag, Plus, FileText, CalendarClock, CheckCircle2 } from 'lucide-react';
import { ehUltimoMes, textoPeriodo, type AvisoUc, type SituacaoAviso } from '../../../../src/lib/avisosUc';
import type { CienciaAviso } from '../../../../src/services/avisosUc';

// Um aviso da UC. Usado na aba "Avisos" da UC e no topo das telas de validação,
// cálculo e emissão (LembretesViewer).
interface Props {
    aviso: AvisoUc;
    /** Mês da fatura aberta ("OUT/2026"), para marcar "último mês". */
    mesFatura?: string | null;
    situacao?: SituacaoAviso;
    /** "Ciente" desta fatura (telas do cálculo) ou o mais recente (aba da UC). */
    ciencia?: CienciaAviso | null;
    cienciaEhDestaFatura?: boolean;
    /** Desconto do cadastro da UC, para comparar com aviso de desconto. */
    descontoCadastro?: string | null;
    acoes?: React.ReactNode;
}

const ESTILO_TIPO: Record<string, { badge: string; icone: string; Icone: React.ElementType; rotulo: string }> = {
    desconto: { badge: 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200', icone: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', Icone: Tag, rotulo: 'Desconto' },
    acrescimo: { badge: 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200', icone: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', Icone: Plus, rotulo: 'Acréscimo' },
    outro: { badge: 'bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200', icone: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', Icone: FileText, rotulo: 'Outro' },
};

const SITUACAO: Record<SituacaoAviso, { rotulo: string; classe: string } | null> = {
    ativo: null,
    futuro: { rotulo: 'Ainda não começou', classe: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' },
    terminado: { rotulo: 'Prazo terminou', classe: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200' },
    encerrado: { rotulo: 'Encerrado', classe: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200' },
};

const dataBR = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '');
const LIMITE_TEXTO = 240;

const AvisoCard: React.FC<Props> = ({ aviso, mesFatura, situacao = 'ativo', ciencia, cienciaEhDestaFatura, descontoCadastro, acoes }) => {
    const [aberto, setAberto] = useState(false);
    const estilo = ESTILO_TIPO[aviso.tipo] ?? ESTILO_TIPO.outro;
    const ultimo = mesFatura ? ehUltimoMes(aviso, mesFatura) : false;
    const apagado = situacao === 'encerrado' || situacao === 'terminado';
    const longo = (aviso.descricao || '').length > LIMITE_TEXTO;
    const etiqueta = SITUACAO[situacao];

    return (
        <div
            data-aviso={aviso.id}
            className={`rounded-xl border p-3.5 flex flex-col md:flex-row gap-3 justify-between md:items-center ${apagado
                ? 'bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700'
                : 'bg-orange-50 dark:bg-orange-900/10 border-orange-200 dark:border-orange-800/30'}`}
        >
            <div className="flex gap-3 items-start min-w-0">
                <div className={`p-2 rounded-lg shrink-0 ${estilo.icone} ${apagado ? 'opacity-60' : ''}`}>
                    <estilo.Icone size={18} />
                </div>
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${estilo.badge}`}>{estilo.rotulo}</span>
                        <span className="text-xs font-medium text-orange-700/80 dark:text-orange-400/80 flex items-center gap-1">
                            <CalendarClock size={12} /> {textoPeriodo(aviso)}
                        </span>
                        {ultimo && (
                            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 dark:bg-amber-700 dark:text-amber-100">
                                Último mês deste aviso
                            </span>
                        )}
                        {etiqueta && <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${etiqueta.classe}`}>{etiqueta.rotulo}</span>}
                    </div>
                    <p className={`text-sm text-gray-800 dark:text-gray-200 mt-1.5 font-medium whitespace-pre-line break-words ${longo && !aberto ? 'line-clamp-3' : ''}`}>
                        {aviso.descricao}
                    </p>
                    {longo && (
                        <button type="button" onClick={() => setAberto(v => !v)} className="text-xs font-semibold text-primary hover:underline mt-0.5">
                            {aberto ? 'Mostrar menos' : 'Ler tudo'}
                        </button>
                    )}
                    {descontoCadastro && aviso.tipo === 'desconto' && !apagado && (
                        <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                            Desconto no cadastro da UC: <strong>{descontoCadastro}</strong>. Se este desconto vale todo mês, mude no cadastro (aba Dados Cadastrais): o cálculo preenche sozinho.
                        </p>
                    )}
                    <p className="text-[11px] text-orange-700/70 dark:text-orange-400/70 mt-1 font-semibold">
                        {aviso.criado_por_nome ? `Criado por ${aviso.criado_por_nome}` : 'Criado'}{aviso.created_at ? ` em ${dataBR(aviso.created_at)}` : ''}
                        {situacao === 'encerrado' && (
                            <span className="text-gray-600 dark:text-gray-300">
                                {' · '}Encerrado{aviso.encerrado_por_nome ? ` por ${aviso.encerrado_por_nome}` : ''}{aviso.data_conclusao ? ` em ${dataBR(aviso.data_conclusao)}` : ''}
                                {aviso.motivo_descarte ? ` — ${aviso.motivo_descarte}` : ''}
                            </span>
                        )}
                    </p>
                    {ciencia && (
                        <p className="text-[11px] text-emerald-700 dark:text-emerald-400 mt-0.5 font-semibold flex items-center gap-1">
                            <CheckCircle2 size={12} />
                            {cienciaEhDestaFatura ? 'Ciente nesta fatura' : `Último ciente: ${ciencia.mes_referencia}`}
                            {ciencia.usuario_nome ? ` — ${ciencia.usuario_nome}` : ''}, {dataBR(ciencia.created_at)}
                        </p>
                    )}
                </div>
            </div>
            {acoes && <div className="flex items-center gap-2 shrink-0 self-end md:self-auto">{acoes}</div>}
        </div>
    );
};

export default AvisoCard;
